import {
  Box,
  DataFieldLabel,
  DataFieldList,
  DataFieldValue,
  EncounterBlockerSchema,
  EncounterLifecycleSchema,
  EncounterOwnerSchema,
  EncounterWorkflowStatusTag,
  Enumerate,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  IconButton,
  InfoHoverCard,
  NoneContent,
  Redact,
  RelativeTimestamp,
  Separator,
  Stack,
  StatusPill,
  StatusPillLead,
  StatusPillTail,
  TextLink,
} from '../../../../core'
import type { StatusPillVariant } from '../../../../core'
import { cn, createEnumSchema, focusRingClassName, tv } from '@joyful/utils'
import { useHasUserCapability } from '../../../hooks/useHasUserCapability'
import type { HasCapability } from '../../../hooks/useHasUserCapability'
import { useRouterOrgSlug } from '../../../utils/orgSlug'
import {
  getEncounterTransitionSourceHref,
  getEncounterTransitionSourceLabel,
} from '../utils/encounterTransitionSourceHrefs'
import type { EncounterTransitionSource } from '../utils/encounterTransitionSourceHrefs'
import { NotionIcon } from './agent/NotionIcon'
import { EncounterOwnerOverridePopover } from './EncounterOwnerOverridePopover'
import type { EncounterOwnerOverridePopoverProps } from './EncounterOwnerOverridePopover'
import type { LucideIcon } from 'lucide-react'
import { BanIcon, CornerDownRightIcon } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { useState } from 'react'
import type { components } from '@joyful/openapi/types.gen'

const factHoverOpenDelayMs = 100

const WORKFLOW_STATUS_BLUEPRINT_URL =
  'https://app.notion.com/p/joyful-health/Blueprint-Retiring-workflow-statuses-in-favor-of-an-agent-first-model-3d10f4731c828102a577f71013330d93#e7eb1d661d704d17a5f7c42da077e42f'

export type EncounterOperationalFactsEncounter = Pick<
  components['schemas']['EncounterAPIResponse'],
  | 'blocker'
  | 'id'
  | 'lifecycle'
  | 'owner'
  | 'workflow_status'
  | 'workflow_status_display'
  | 'workflow_status_simplified'
  | 'workflow_status_simplified_display'
>

type EncounterLifecycle = NonNullable<
  EncounterOperationalFactsEncounter['lifecycle']
>
type OutcomeEncounterLifecycle = Exclude<EncounterLifecycle, { type: 'OPEN' }>
type OutsideEncounterBlocker = Exclude<
  NonNullable<EncounterOperationalFactsEncounter['blocker']>,
  { type: 'NONE' }
>
type EncounterOwner = components['schemas']['EncounterOwner']

const factMeanings = {
  blocker: `Which outside party prevents the next move, why, and since when?`,
  lifecycle: `Is the encounter open, resolved and waiting for human review, or closed?`,
  owner: `Which Joyful team is directly responsible for the next move?`,
} as const

export const EncounterResolutionOutcomeSchema = createEnumSchema<
  components['schemas']['EncounterResolutionOutcome']
>([
  { title: `Recovered`, value: 'RESOLVED' },
  { title: `Payment posting only`, value: 'PAYMENT_POSTING_ONLY' },
  { title: `Uncollectible`, value: 'UNCOLLECTIBLE' },
  { title: `No action required`, value: 'NO_ACTION_REQUIRED' },
])

const lifecycleOutcomeDisplay = {
  CLOSED: { label: `Closed`, variant: 'success' },
  RESOLUTION_PROPOSED: { label: `Resolved`, variant: 'resolved' },
} as const

type FactBadgeSize = 'sm' | 'xs'

const factIconVariants = tv({
  base: 'size-3 shrink-0',

  variants: {
    tone: {
      content: 'text-tremor-content',
      emphasis: 'text-tremor-content-emphasis',
      resolved: 'text-category-violet',
      success: 'text-category-emerald',
    },
  },
})

type FactIconTone = 'content' | 'emphasis' | 'resolved' | 'success'

const factDetailsVariants = tv({
  base: 'col-span-2 ml-3 py-0.5 pr-1.5',
})

function FactSegment({
  children,
  icon: Icon,
  label,
  part,
  variant,
  ...props
}: Omit<ComponentProps<'span'>, 'children' | 'className' | 'ref'> & {
  children: string
  icon?: LucideIcon
  label: string
  part: 'lead' | 'tail'
  variant: StatusPillVariant
}) {
  const Segment = part === 'tail' ? StatusPillTail : StatusPillLead
  const startAdornment = Icon ? (
    <Icon aria-hidden="true" className="size-3 shrink-0" />
  ) : null

  return (
    <Segment
      aria-label={label}
      startAdornment={startAdornment}
      variant={variant}
      {...props}
    >
      {children}
    </Segment>
  )
}

function LifecycleSegments({
  lifecycle,
}: {
  lifecycle: Pick<OutcomeEncounterLifecycle, 'outcome' | 'type'>
}) {
  const display = lifecycleOutcomeDisplay[lifecycle.type]
  const outcomeTitle = EncounterResolutionOutcomeSchema.meta(
    lifecycle.outcome,
  ).title

  return (
    <>
      <FactSegment
        icon={EncounterLifecycleSchema.meta(lifecycle.type).icon}
        label={`Lifecycle: ${display.label}`}
        part="lead"
        variant={display.variant}
      >
        {display.label}
      </FactSegment>
      <FactSegment
        label={`Outcome: ${outcomeTitle}`}
        part="tail"
        variant={display.variant}
      >
        {outcomeTitle}
      </FactSegment>
    </>
  )
}

function OwnerSegment({
  owner,
  ...props
}: Omit<
  ComponentProps<typeof FactSegment>,
  'children' | 'label' | 'part' | 'variant'
> & {
  owner: EncounterOwner
}) {
  const metadata = EncounterOwnerSchema.meta(owner)

  return (
    <FactSegment
      {...props}
      icon={metadata.icon}
      label={`Owner: ${metadata.title}`}
      part="lead"
      variant="neutral"
    >
      {metadata.title}
    </FactSegment>
  )
}

function BlockerSegment({
  blockerType,
}: {
  blockerType: OutsideEncounterBlocker['type']
}) {
  const title = EncounterBlockerSchema.meta(blockerType).title

  return (
    <FactSegment
      icon={BanIcon}
      label={`Blocker: ${title}`}
      part="lead"
      variant="brand"
    >
      {title}
    </FactSegment>
  )
}

export interface EncounterFactSegmentsProps {
  blockerType: OutsideEncounterBlocker['type'] | null
  lifecycle: Pick<OutcomeEncounterLifecycle, 'outcome' | 'type'> | null
  owner: EncounterOwner | null
  ownerOverride?: Pick<
    EncounterOwnerOverridePopoverProps,
    'encounterId' | 'onOpenChange'
  >
  size: FactBadgeSize
}

export function EncounterFactSegments({
  blockerType,
  lifecycle,
  owner,
  ownerOverride,
  size,
}: EncounterFactSegmentsProps) {
  const ownerSegment = owner ? <OwnerSegment owner={owner} /> : null

  return (
    <StatusPill join="group" overflowBehavior="none" size={size}>
      {lifecycle ? <LifecycleSegments lifecycle={lifecycle} /> : null}
      {owner && ownerOverride ? (
        <EncounterOwnerOverridePopover
          encounterId={ownerOverride.encounterId}
          fallback={ownerSegment}
          owner={owner}
          onOpenChange={ownerOverride.onOpenChange}
        >
          <OwnerSegment owner={owner} />
        </EncounterOwnerOverridePopover>
      ) : (
        ownerSegment
      )}
      {blockerType ? <BlockerSegment blockerType={blockerType} /> : null}
    </StatusPill>
  )
}

function EncounterFactsHoverCard({
  encounter,
}: {
  encounter: EncounterOperationalFactsEncounter
}) {
  const hasCapability = useHasUserCapability()
  const blocker = outsideBlocker(encounter.blocker)
  const legacyStatus = legacyWorkflowStatus(encounter, hasCapability)

  return (
    <Stack>
      <Box padding="sm">
        <DataFieldList labelWidth="fit" size="sm">
          <FactRow
            details={lifecycleFactDetails({
              encounterId: encounter.id,
              lifecycle: encounter.lifecycle,
            })}
            label="Lifecycle"
            meaning={factMeanings.lifecycle}
          >
            <LifecycleFact lifecycle={encounter.lifecycle} />
          </FactRow>
          <FactRow label="Owner" meaning={factMeanings.owner}>
            {encounter.owner ? (
              <FactHeadline
                icon={EncounterOwnerSchema.meta(encounter.owner).icon}
                title={EncounterOwnerSchema.meta(encounter.owner).title}
                tone="emphasis"
              />
            ) : (
              <FactNone />
            )}
          </FactRow>
          <FactRow
            details={blockerFactDetails({
              blocker,
              encounterId: encounter.id,
            })}
            label="Blocker"
            meaning={factMeanings.blocker}
          >
            <BlockerFact blocker={blocker} />
          </FactRow>
        </DataFieldList>
      </Box>
      {legacyStatus.type === 'hidden' ? null : (
        <>
          <Separator />
          <Box padding="md">
            <LegacyWorkflowStatusSection status={legacyStatus} />
          </Box>
        </>
      )}
    </Stack>
  )
}

function LifecycleFact({
  lifecycle,
}: {
  lifecycle: EncounterOperationalFactsEncounter['lifecycle']
}) {
  if (!lifecycle) {
    return <FactNone />
  }

  const metadata = EncounterLifecycleSchema.meta(lifecycle.type)
  if (lifecycle.type === 'OPEN') {
    return (
      <FactHeadline
        icon={metadata.icon}
        title={metadata.title}
        tone="content"
      />
    )
  }

  return (
    <FactHeadline
      detail={EncounterResolutionOutcomeSchema.meta(lifecycle.outcome).title}
      icon={metadata.icon}
      title={lifecycleOutcomeDisplay[lifecycle.type].label}
      tone={lifecycleOutcomeDisplay[lifecycle.type].variant}
    />
  )
}

function lifecycleFactDetails({
  encounterId,
  lifecycle,
}: {
  encounterId: string
  lifecycle: EncounterOperationalFactsEncounter['lifecycle']
}) {
  if (!lifecycle || lifecycle.type === 'OPEN') {
    return null
  }

  const changedAt =
    lifecycle.type === 'CLOSED' ? lifecycle.closed_at : lifecycle.proposed_at
  const returnOwnerTitle =
    lifecycle.type === 'RESOLUTION_PROPOSED'
      ? EncounterOwnerSchema.meta(lifecycle.prior_human_return_owner).title
      : null

  return (
    <FactDetails
      encounterId={encounterId}
      occurredAt={changedAt}
      source={lifecycle.source}
    >
      {returnOwnerTitle ? (
        <FactDetailNote>
          {`Returned to ${returnOwnerTitle} if QA fails`}
        </FactDetailNote>
      ) : null}
    </FactDetails>
  )
}

function BlockerFact({ blocker }: { blocker: OutsideEncounterBlocker | null }) {
  if (!blocker) {
    return <FactNone />
  }

  return (
    <FactHeadline
      icon={BanIcon}
      title={EncounterBlockerSchema.meta(blocker.type).title}
    />
  )
}

function blockerFactDetails({
  blocker,
  encounterId,
}: {
  blocker: OutsideEncounterBlocker | null
  encounterId: string
}) {
  if (!blocker) {
    return null
  }

  return (
    <FactDetails
      encounterId={encounterId}
      occurredAt={blocker.blocked_at}
      source={blocker.source}
    >
      <FactDetailNote>{blocker.reason}</FactDetailNote>
    </FactDetails>
  )
}

function FactDetails({
  children,
  encounterId,
  occurredAt,
  source,
}: {
  children?: ReactNode
  encounterId: string
  occurredAt: string
  source: EncounterTransitionSource
}) {
  return (
    <Stack gap="xs">
      {children}
      <Stack direction="horizontal" justify="end">
        <FactEvidenceLine
          encounterId={encounterId}
          occurredAt={occurredAt}
          source={source}
        />
      </Stack>
    </Stack>
  )
}

function FactDetailNote({ children }: { children: ReactNode }) {
  return (
    <Stack align="start" direction="horizontal" gap="xs">
      <CornerDownRightIcon
        aria-hidden
        className="text-tremor-content size-3 shrink-0"
      />
      <Redact asChild>
        <p className="text-tremor-content text-xs italic">{children}</p>
      </Redact>
    </Stack>
  )
}

function FactHeadline({
  detail,
  icon: Icon,
  title,
  tone,
}: {
  detail?: string
  icon: LucideIcon
  title: string
  tone?: FactIconTone
}) {
  return (
    <Stack
      align="center"
      direction="horizontal"
      gap="xs"
      justify="end"
      wrap="wrap"
    >
      <Icon aria-hidden="true" className={factIconVariants({ tone })} />
      <span className="font-medium">{title}</span>
      {detail ? (
        <span className="text-tremor-content">{`· ${detail}`}</span>
      ) : null}
    </Stack>
  )
}

function FactNone() {
  return (
    <Stack direction="horizontal" justify="end">
      <NoneContent />
    </Stack>
  )
}

function FactEvidenceLine({
  encounterId,
  occurredAt,
  source,
}: {
  encounterId: string
  occurredAt: string
  source: EncounterTransitionSource
}) {
  const orgSlug = useRouterOrgSlug()
  const href = getEncounterTransitionSourceHref({
    encounterId,
    orgSlug,
    source,
  })
  const label = getEncounterTransitionSourceLabel(source)

  return (
    <span className="text-tremor-content text-tremor-label">
      <Enumerate separator=" · ">
        <RelativeTimestamp timestamp={occurredAt} />
        {href ? (
          <TextLink to={href} underlineVariant="hover">
            {label}
          </TextLink>
        ) : (
          label
        )}
      </Enumerate>
    </span>
  )
}

function outsideBlocker(
  blocker: EncounterOperationalFactsEncounter['blocker'],
) {
  return blocker && blocker.type !== 'NONE' ? blocker : null
}

function lifecycleWithOutcome(
  lifecycle: EncounterOperationalFactsEncounter['lifecycle'],
) {
  return lifecycle?.type === 'CLOSED' ||
    lifecycle?.type === 'RESOLUTION_PROPOSED'
    ? lifecycle
    : null
}

function LegacyWorkflowStatusSection({
  status,
}: {
  status: Exclude<ReturnType<typeof legacyWorkflowStatus>, { type: 'hidden' }>
}) {
  return (
    <Stack gap="xs">
      <Stack asChild align="center" direction="horizontal" gap="xs">
        <span className="text-tremor-content text-xs">
          {`Legacy workflow status`}
          <IconButton
            aria-label="Open the workflow status blueprint"
            asChild
            size="inline"
            variant="ghost"
          >
            <a
              href={WORKFLOW_STATUS_BLUEPRINT_URL}
              rel="noreferrer"
              target="_blank"
            >
              <NotionIcon />
            </a>
          </IconButton>
        </span>
      </Stack>
      {status.type === 'status' ? (
        <EncounterWorkflowStatusTag
          overflowBehavior="none"
          size="sm"
          workflowStatus={status.workflowStatus}
          workflowStatusDisplay={status.workflowStatusDisplay}
        />
      ) : (
        <NoneContent />
      )}
    </Stack>
  )
}

function FactRow({
  children,
  details,
  label,
  meaning,
}: {
  children: ReactNode
  details?: ReactNode
  label: string
  meaning: string
}) {
  const info = (
    <span className="opacity-0 group-focus-within/fact:opacity-100 group-hover/fact:opacity-100 has-data-[state=open]:opacity-100">
      <InfoHoverCard
        openDelay={factHoverOpenDelayMs}
        size="inline"
        tone="content"
      >
        {meaning}
      </InfoHoverCard>
    </span>
  )

  return (
    <div className="group/fact col-span-2 grid grid-cols-subgrid">
      <div className="col-span-2 grid grid-cols-subgrid items-center px-1.5 py-1">
        <DataFieldLabel>
          <Stack align="center" direction="horizontal" gap="xs">
            <span>{label}</span>
            {info}
          </Stack>
        </DataFieldLabel>
        <DataFieldValue>{children}</DataFieldValue>
      </div>
      {details ? <div className={factDetailsVariants()}>{details}</div> : null}
    </div>
  )
}

function legacyWorkflowStatus(
  encounter: EncounterOperationalFactsEncounter,
  hasCapability: HasCapability,
) {
  if (hasCapability('encounter.workflow_status.view.complete')) {
    if (!encounter.workflow_status) {
      return { type: 'empty' } as const
    }

    return {
      type: 'status',
      workflowStatus: encounter.workflow_status,
      workflowStatusDisplay: encounter.workflow_status_display,
    } as const
  }

  if (!hasCapability('encounter.workflow_status.view')) {
    return { type: 'hidden' } as const
  }

  if (!encounter.workflow_status_simplified) {
    return { type: 'empty' } as const
  }

  return {
    type: 'status',
    workflowStatus: encounter.workflow_status_simplified,
    workflowStatusDisplay: encounter.workflow_status_simplified_display,
  } as const
}

export function hasVisibleOperationalFacts(
  encounter: EncounterOperationalFactsEncounter,
) {
  const lifecycle = lifecycleWithOutcome(encounter.lifecycle)
  const blocker = outsideBlocker(encounter.blocker)
  return Boolean(lifecycle || encounter.owner || blocker)
}

export interface EncounterOperationalFactsProps {
  encounter: EncounterOperationalFactsEncounter
  isOwnerEditable?: boolean
  size?: FactBadgeSize
}

export function EncounterOperationalFacts({
  encounter,
  isOwnerEditable = false,
  size = 'sm',
}: EncounterOperationalFactsProps) {
  const [isHoverCardOpen, setHoverCardOpen] = useState(false)
  const [isOwnerOverrideOpen, setOwnerOverrideOpen] = useState(false)
  if (!hasVisibleOperationalFacts(encounter)) {
    return null
  }

  const lifecycle = lifecycleWithOutcome(encounter.lifecycle)
  const blocker = outsideBlocker(encounter.blocker)

  return (
    <HoverCard
      open={isHoverCardOpen && !isOwnerOverrideOpen}
      openDelay={factHoverOpenDelayMs}
      onOpenChange={setHoverCardOpen}
    >
      <HoverCardTrigger asChild>
        <span
          aria-label={`Lifecycle, owner, and blocker`}
          className={cn(
            'inline-flex max-w-full min-w-0 items-stretch rounded-lg',
            focusRingClassName,
          )}
          role="group"
          tabIndex={0}
        >
          <EncounterFactSegments
            blockerType={blocker?.type ?? null}
            lifecycle={lifecycle}
            owner={encounter.owner ?? null}
            ownerOverride={
              isOwnerEditable
                ? {
                    encounterId: encounter.id,
                    onOpenChange: setOwnerOverrideOpen,
                  }
                : undefined
            }
            size={size}
          />
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        padding="none"
        side="top"
        size="sm"
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        <EncounterFactsHoverCard encounter={encounter} />
      </HoverCardContent>
    </HoverCard>
  )
}
